import { useMemo } from 'react';
import { useDomainSelection } from '../../../context';
import { BillingCycle } from './Plan';
import { LicenseDetails, PlanType } from './types';

interface PlanPricingResult {
  originalPrice: number;
  discountedPrice?: number;
  savings?: number;
}

export const usePlanPricing = (
  plan: PlanType,
  billingCycle: BillingCycle,
  license?: LicenseDetails,
): PlanPricingResult => {
  const { currentAccessibeAccount } = useDomainSelection();

  return useMemo(() => {
    if (!plan || !billingCycle || plan.name?.toLowerCase() === 'enterprise') {
      return {
        originalPrice: 0,
        discountedPrice: 0,
        savings: 0,
      };
    }

    const price = billingCycle === 'annually' ? plan.price : plan.monthlyPrice;

    const isPartner = currentAccessibeAccount?.partner;
    const discount =
      isPartner && !license!.discountEnabled ? 0 : license!.finalDiscount ?? 0;

    const hasCustomPrice = license && license.customPrice > 0;

    let finalPrice = price;
    const originalPrice = price;
    let discountPrice = 0;

    if (hasCustomPrice) {
      finalPrice = license.customPrice;

      return {
        originalPrice: finalPrice,
        discountedPrice: finalPrice,
        savings: 0,
      };
    } else {
      const priceWithDiscount = Math.round(
        originalPrice - (originalPrice / 100) * discount,
      );

      discountPrice = originalPrice - priceWithDiscount;
      finalPrice = priceWithDiscount;

      return {
        originalPrice: originalPrice,
        discountedPrice: finalPrice,
        savings: discountPrice,
      };
    }
  }, [plan, billingCycle, license, currentAccessibeAccount]);
};
